import React, { useMemo, useState } from "react";
import moment from "moment-timezone";

import "./trip-approval-strip.css";

const TripApprovalReset = ({ trip, onReset, disabled, canReset }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const status = trip?.approvalStatus;

  const hoursSinceDecision = useMemo(() => {
    if (!trip?.approvalDecisionAt) return null;
    const decidedAt = moment(trip.approvalDecisionAt);
    return moment().diff(decidedAt, "hours", true);
  }, [trip?.approvalDecisionAt]);

  if (!canReset || !trip || !status) return null;

  const decidedByName = trip.approvalDecisionBy?.name || "—";
  const decidedAtLabel = trip.approvalDecisionAt
    ? moment(trip.approvalDecisionAt).format("MMM D, YYYY · h:mm A")
    : null;

  const requiresExtraConfirm =
    hoursSinceDecision !== null && hoursSinceDecision > 24;

  const handleOpen = () => {
    if (disabled || busy) return;
    setReason("");
    setConfirmOpen(true);
  };

  const handleCancel = () => {
    if (busy) return;
    setConfirmOpen(false);
  };

  const handleConfirm = async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      await onReset(reason.trim() || undefined);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="trip-approval-reset"
      aria-label="Reset approval"
    >
      <div className="trip-approval-reset__meta">
        <span
          className={
            "trip-approval-reset__status trip-approval-reset__status--" + status
          }
        >
          <span
            className="trip-approval-reset__status-dot"
            aria-hidden="true"
          />
          {status === "approved" ? "Approved" : "Declined"}
        </span>
        <span className="trip-approval-reset__detail">
          by <strong>{decidedByName}</strong>
        </span>
        {decidedAtLabel && (
          <span className="trip-approval-reset__timestamp">
            · {decidedAtLabel}
          </span>
        )}
      </div>

      <button
        type="button"
        className="trip-approval-reset__btn"
        onClick={handleOpen}
        disabled={disabled || busy || confirmOpen}
      >
        Reset approval
      </button>

      <div
        className={
          "trip-approval-reset__panel-wrapper" +
          (confirmOpen ? " trip-approval-reset__panel-wrapper--open" : "")
        }
      >
        <div className="trip-approval-reset__panel-inner">
          <div className="trip-approval-reset__panel">
            <h4 className="trip-approval-reset__panel-title">
              Reset this trip to In Review?
            </h4>
            <p className="trip-approval-reset__panel-body">
              The <strong>{status}</strong> decision will be cleared and the
              trip will return to In Review for re-evaluation. The original
              decision stays in the audit trail.
              {status === "declined" && (
                <>
                  {" "}Archived flight legs may need manual reactivation by ops.
                </>
              )}
              {requiresExtraConfirm && (
                <>
                  {" "}<strong>This decision is more than 24 hours old</strong>
                  {" "}— double-check before resetting.
                </>
              )}
            </p>
            <textarea
              className="trip-approval-reset__reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for reset (optional, saved to audit trail)"
              rows={2}
              disabled={busy}
            />
            <div className="trip-approval-reset__panel-actions">
              <button
                type="button"
                className="trip-approval-reset__action trip-approval-reset__action--ghost"
                onClick={handleCancel}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="trip-approval-reset__action trip-approval-reset__action--primary"
                onClick={handleConfirm}
                disabled={busy || disabled}
              >
                {busy ? "Resetting…" : "Confirm Reset"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripApprovalReset;
